import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteFooter from "../components/SiteFooter";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

// Phase 2 compression (Feb 2026): cut onboarding from 5 → 3 steps. The original
// sponsors step is gone — we infer categories from the AI suggest endpoint at
// finish() time so the athlete doesn't have to make that decision before they
// see the dashboard. They can still edit categories from Profile/Settings.
const STEPS = [
  { key: "profile", title: "Your profile" },
  { key: "audience", title: "Your audience" },
  { key: "story", title: "Your story" },
];

const CATEGORIES = ["Apparel","Nutrition","Fitness Tech","Recovery","Energy Drink","Eyewear","Footwear","Gaming","Crypto/Fintech","Lifestyle","Auto","Local Business"];

export default function Onboarding() {
  const { refresh, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    sport: "", location: "", level: "", club: "", achievements: "",
    audience_size: "", instagram_url: "", tiktok_url: "", youtube_url: "", linkedin_url: "",
    target_sponsor_categories: [], personal_story: "", sponsorship_goals: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [finishMsg, setFinishMsg] = useState("");
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!user) return;
    if (user.onboarded) navigate("/app/dashboard");
    else if (user.account_type === "brand") navigate("/onboarding/brand");
    else if (user.account_type === "agent" || user.account_type === "club") navigate("/onboarding/agency");
  }, [user, navigate]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  // Finish: fire the AI sponsor-category suggestion silently, merge into the
  // payload, then complete onboarding. If the LLM call fails we still complete
  // — the athlete can pick categories later from Profile. The 'welcome=1' query
  // param triggers the Welcome Lap tour on the dashboard.
  const finish = async () => {
    setSubmitting(true);
    setFinishMsg("Personalising your dashboard…");
    let categories = form.target_sponsor_categories;
    try {
      if (categories.length === 0 && form.sport && form.sport.trim()) {
        try {
          const { data } = await api.post("/ai/onboarding/suggest-sponsor-categories", {
            sport: form.sport,
            level: form.level,
            audience_size: form.audience_size,
            achievements: form.achievements,
            primary_platform: "",
          });
          const picks = (data.categories || []).filter(c => CATEGORIES.includes(c));
          if (picks.length) categories = picks;
        } catch {
          // Soft-fail: leave categories empty; user can fill them later.
        }
      }
      await api.post("/onboarding/complete", { ...form, target_sponsor_categories: categories });
      await refresh();
      navigate("/app/dashboard?welcome=1");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-white flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line flex justify-between items-center">
        <BrandLogo variant="wordmark" size={28} />
        <div className="label-eyebrow">Step {step+1} / {STEPS.length}</div>
      </div>
      <div className="h-1 w-full bg-line">
        <div className="h-1 bg-volt transition-all" style={{ width: `${((step+1)/STEPS.length)*100}%` }} />
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-12 py-12">
        <div className="label-eyebrow">{STEPS[step].key}</div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-3" data-testid="onboarding-step-title">{STEPS[step].title}</h1>
        {step === 0 && (
          <p className="text-sm text-ink-muted mt-3 max-w-2xl">Tell us who you are and what you do. We&apos;ll use this to suggest sponsor categories automatically — no more guesswork.</p>
        )}

        <div className="mt-10 space-y-6">
          {step === 0 && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name"><Input value={form.full_name} onChange={(v)=>upd("full_name", v)} testid="onb-full-name" /></Field>
                <Field label="Location"><Input value={form.location} onChange={(v)=>upd("location", v)} placeholder="London, UK" testid="onb-location" /></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Primary sport"><Input value={form.sport} onChange={(v)=>upd("sport", v)} placeholder="Football, MMA, Athletics…" testid="onb-sport" /></Field>
                <Field label="Current level"><Input value={form.level} onChange={(v)=>upd("level", v)} placeholder="Semi-pro, National, Regional…" testid="onb-level" /></Field>
              </div>
              <Field label="Team / club"><Input value={form.club} onChange={(v)=>upd("club", v)} testid="onb-club" /></Field>
              <Field label="Achievements"><Textarea value={form.achievements} onChange={(v)=>upd("achievements", v)} placeholder="Titles, ranks, records, key results…" testid="onb-achievements" /></Field>
            </>
          )}
          {step === 1 && (
            <>
              <Field label="Total audience size (across platforms)"><Input value={form.audience_size} onChange={(v)=>upd("audience_size", v)} placeholder="e.g. 25,000" testid="onb-audience" /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Instagram URL"><Input value={form.instagram_url} onChange={(v)=>upd("instagram_url", v)} placeholder="https://instagram.com/handle" testid="onb-ig" /></Field>
                <Field label="TikTok URL"><Input value={form.tiktok_url} onChange={(v)=>upd("tiktok_url", v)} placeholder="https://tiktok.com/@handle" testid="onb-tt" /></Field>
                <Field label="YouTube URL"><Input value={form.youtube_url} onChange={(v)=>upd("youtube_url", v)} testid="onb-yt" /></Field>
                <Field label="LinkedIn URL"><Input value={form.linkedin_url} onChange={(v)=>upd("linkedin_url", v)} testid="onb-li" /></Field>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Short personal story"><Textarea rows={5} value={form.personal_story} onChange={(v)=>upd("personal_story", v)} placeholder="The 60-second version of your journey…" testid="onb-story" /></Field>
              <Field label="Sponsorship goals"><Textarea rows={3} value={form.sponsorship_goals} onChange={(v)=>upd("sponsorship_goals", v)} placeholder="What deals do you want this year?" testid="onb-goals" /></Field>
              <div className="border border-volt/40 bg-volt/5 px-4 py-3 flex items-start gap-3" data-testid="onb-ai-hint">
                <Sparkles className="w-4 h-4 text-volt shrink-0 mt-0.5" />
                <div className="text-xs text-ink-muted leading-relaxed">
                  When you hit <span className="text-white font-medium">Finish setup</span>, we&apos;ll AI-suggest 3–5 sponsor categories from your sport &amp; audience data. Edit anytime in <span className="text-white">Profile → Sponsors</span>.
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-12 flex items-center justify-between gap-4">
          <button onClick={prev} disabled={step === 0} className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-30" data-testid="onb-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {submitting && finishMsg && (
            <div className="text-xs text-ink-muted flex items-center gap-2" data-testid="onb-finish-status">
              <Sparkles className="w-3 h-3 text-volt animate-pulse" /> {finishMsg}
            </div>
          )}
          {step < STEPS.length - 1 ? (
            <button onClick={next} className="btn-volt inline-flex items-center gap-2" data-testid="onb-next-btn">Next <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={finish} disabled={submitting} className="btn-volt inline-flex items-center gap-2 disabled:opacity-60" data-testid="onb-finish-btn">
              {submitting ? "Saving…" : "Finish setup"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function Input({ value, onChange, placeholder = "", testid }) {
  return <input value={value} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />;
}
function Textarea({ value, onChange, placeholder = "", rows = 4, testid }) {
  return <textarea rows={rows} value={value} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />;
}
